import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, CheckCircle2, Circle } from "lucide-react";
import {
  PageContainer, PageHeader, Card, SectionLabel, StepItem,
  Button, Input, Label, Field, Loading, EmptyState,
} from "../components/kit";

const ROUNDS = ["hr", "technical", "behavioral"];
const ROUND_LABELS = { hr: "HR", technical: "Technical", behavioral: "Behavioral" };
const MAX_ROLES = 2;

export default function CustomRoles() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/candidate/roles");
      setRoles(data);
    } catch {
      toast.error("Could not load custom roles");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRoles(); }, []);

  const performCreate = async () => {
    if (!createName.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/candidate/roles", { name: createName.trim() });
      toast.success(`Role "${data.name}" created`);
      setCreateOpen(false);
      setCreateName("");
      navigate(`/custom-roles/${data.role_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create role");
    } finally { setBusy(false); }
  };

  const performDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/candidate/roles/${deleteTarget.role_id}`);
      toast.success(`"${deleteTarget.name}" deleted`);
      setRoles((arr) => arr.filter((r) => r.role_id !== deleteTarget.role_id));
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete role");
    } finally { setBusy(false); }
  };

  const atLimit = roles.length >= MAX_ROLES;

  return (
    <PageContainer width="mid">
      <PageHeader
        back={{ to: "/dashboard", label: "Dashboard" }}
        title="Custom Interviews"
        subtitle={`Create up to ${MAX_ROLES} private roles, upload your own docs, and build question sets.`}
        actions={
          <Button onClick={() => setCreateOpen(true)} disabled={atLimit}>
            <Plus className="w-4 h-4" />
            {atLimit ? `Limit reached (${MAX_ROLES}/${MAX_ROLES})` : "New role"}
          </Button>
        }
      />

      {/* How it works */}
      <Card className="bg-secondary/30 mb-8" padding="p-5">
        <SectionLabel>How it works</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <StepItem n="1" color="#0025D8" title="Create a role">
            Name your target job — e.g. "Senior React Engineer" or "Product Manager".
          </StepItem>
          <StepItem n="2" color="#0F172A" title="Upload docs & pick questions">
            Upload reference material, let AI generate tailored questions, then hand-pick up to 5 per round.
          </StepItem>
          <StepItem n="3" color="#7C3AED" title="Start interview">
            Practice with your custom set across HR, Technical, and Behavioral rounds.
          </StepItem>
        </div>
      </Card>

      {loading ? (
        <Loading />
      ) : roles.length === 0 ? (
        <EmptyState
          icon={Plus}
          title="No custom roles yet."
          body="Create a role, upload reference documents, add questions, and start practising."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4" /> Create your first role
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {roles.map((role) => (
            <Card key={role.role_id} hover padding="p-5" className="flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-xl text-[#0F172A]">{role.name}</div>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  {ROUNDS.map((rnd) => {
                    const docs = role[`${rnd}_doc_count`] || 0;
                    const qs = role[`${rnd}_question_count`] || 0;
                    const ready = qs > 0;
                    return (
                      <div key={rnd} className="flex items-center gap-1.5">
                        {ready
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
                          : <Circle className="w-3.5 h-3.5 text-muted-foreground" />}
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {ROUND_LABELS[rnd]} · {docs} doc{docs !== 1 ? "s" : ""} · {qs} Q{qs !== 1 ? "s" : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => navigate(`/custom-roles/${role.role_id}`)}>
                  <ChevronRight className="w-3.5 h-3.5" /> Manage
                </Button>
                <Button variant="outlineDanger" size="sm" onClick={() => setDeleteTarget(role)} aria-label="Delete role">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) { setCreateOpen(false); setCreateName(""); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-[#0F172A]">New custom role</DialogTitle>
            <DialogDescription>Give your role a name. You'll configure rounds, documents, and questions next.</DialogDescription>
          </DialogHeader>
          <Field label="Role name">
            <Input autoFocus value={createName} onChange={(e) => setCreateName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") performCreate(); }}
              placeholder="e.g. Senior Frontend Engineer" />
          </Field>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={performCreate} disabled={busy || !createName.trim()} loading={busy}>
              {!busy && <Plus className="w-4 h-4" />} {busy ? "Creating…" : "Create & configure"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-[#E11D48]">Delete role</DialogTitle>
            <DialogDescription>
              Delete <strong>{deleteTarget?.name}</strong>? This removes all documents and questions permanently.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" onClick={performDelete} disabled={busy} loading={busy}>
              {!busy && <Trash2 className="w-4 h-4" />} {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
